<template>
  <layout-page>
    <div class="store_container">
      <div class="flex flex-end">
        <form-field-radio-buttons
          v-model="selectedFilter"
          :items="availableFilters"
          @change="updateSelectedItem"
        ></form-field-radio-buttons>
      </div>

      <tabs-vertical :tab-index="currentTab">
        <b-tab-item
          label="Follow Up Sequences"
          value="followUps"
          icon="account-voice"
        >
          <template v-if="currentTab === 'followUps'">
            <store-asset-grid
              v-if="hasFollowUpSequenceFeature"
              @installed="flagAssetAsInstalled"
              :shared-assets="sharedFollowUpSequences"
              setting-tab="followUp"
            ></store-asset-grid>

            <section-page v-else class="follow_up--empty">
              <div class="upgrade_message">
                <p class="upgrade_message--heading">Upgrade Required</p>
                <p class="upgrade_message--content"><a @click="openBillingPortal">Upgrade your account today</a> and gain access to the complete automated follow up sequences that you can copy & paste into your business today!</p>
                <p class="upgrade_message--content">Congratulations! You're ready to take the next step and start closing more deals with less effort!.</p>
                <p class="upgrade_message--content">Cross channel, automated follow up sequences from ForeFront are always working for you!  It's time to let the system do the heavy lifting when it comes to nurturing your leads. Automated drip sequences can transform your business without spending more on marketing.</p>

                <div class="flex flex-centered">
                  <button-outlined
                    class="upgrade_message--button"
                    @click="openBillingPortal"
                    size="">Upgrade Your Account Now</button-outlined>
                </div>
              </div>
            </section-page>
          </template>
        </b-tab-item>

        <b-tab-item
          label="Pipeline Views"
          value="pipelines"
          icon="view-dashboard"
        >
          <store-asset-grid
            v-if="currentTab === 'pipelines'"
            @installed="flagAssetAsInstalled"
            :shared-assets="sharedPipelineViews"
            setting-tab="pipeline"
          ></store-asset-grid>
        </b-tab-item>

        <b-tab-item
          label="Call Scripts"
          value="callScripts"
          icon="clipboard-outline"
        >
          <store-asset-grid
            v-if="currentTab === 'callScripts'"
            @installed="flagAssetAsInstalled"
            :shared-assets="sharedCallScripts"
            setting-tab="scripts"
          ></store-asset-grid>
        </b-tab-item>

        <b-tab-item
          label="Snippets"
          value="snippets"
          icon="clipboard-text"
        >
          <store-asset-grid
            v-if="currentTab === 'snippets'"
            @installed="flagAssetAsInstalled"
            :shared-assets="sharedSnippets"
            setting-tab="snippets"
          ></store-asset-grid>
        </b-tab-item>
      </tabs-vertical>
    </div>
  </layout-page>
</template>

<script>
import api from "../../api";
import {MODELS} from "../../services/models";
import StoreAssetGrid from "./components/StoreAssetGrid";

export default {
  name: "StorePage",
  metaInfo() {
    return {
      title: this.metaTitle,
    };
  },
  components: {
    StoreAssetGrid,
  },
  data() {
    return {
      currentTab: "followUps",
      sharedAssets: [],
      selectedFilter: "all",
      availableFilters: [
        {
          value: "forefront",
          label: "Forefront"
        },
        {
          value: "shared",
          label: "Shared"
        },
        {
          value: "installed",
          label: "Installed"
        },
        {
          value: "all",
          label: "All"
        },
      ]
    };
  },
  computed: {
    metaTitle() {
      switch (this.currentTab) {
        case "followUps":
          return "Follow Up Sequence Store";
        case "pipelines":
          return "Pipeline View Store";
        case "callScripts":
          return "Call Script Store";
        case "snippets":
          return "Snippet Store";
        default:
          return "Store";
      }
    },
    hasFollowUpSequenceFeature() {
      return this.$store.getters.account.has_follow_up_sequence_feature;
    },
    filteredAssets() {
      if (this.selectedFilter === "forefront") {
        return this.sharedAssets.filter((shareable) => shareable.shared_by_forefront);
      }
      if (this.selectedFilter === "shared") {
        return this.sharedAssets.filter((shareable) => !!shareable.shareable_group_email_id);
      }
      if (this.selectedFilter === "installed") {
        return this.sharedAssets.filter((shareable) => shareable.installed);
      }

      return this.sharedAssets;
    },
    sharedPipelineViews() {
      return this.filteredAssets.filter(sharedAsset => sharedAsset.class_name === MODELS.PipelineView.class_name);
    },
    sharedFollowUpSequences() {
      return this.filteredAssets.filter(sharedAsset => sharedAsset.class_name === MODELS.FollowUpSequenceTemplate.class_name);
    },
    sharedCallScripts() {
      return this.filteredAssets.filter(sharedAsset => sharedAsset.class_name === MODELS.CallScript.class_name);
    },
    sharedSnippets() {
      return this.filteredAssets.filter(sharedAsset => sharedAsset.class_name === MODELS.Snippet.class_name);
    },
  },
  watch: {
    $route() {
      this.setTab();
    },
    currentTabs: function (newTab) {
      if(newTab !== this.$route.params.tab) {
        this.$router.replace({params: {tab: this.currentTab}});
      }
    }
  },
  created() {
    this.setTab();
    api.shareableGroups.sharedAssets().then((response) => {
      this.sharedAssets = response.data;
    });
  },
  methods: {
    setTab() {
      if (this.$route.params.tab) {
        this.currentTab = this.$route.params.tab;
      } else {
        this.$router.replace({params: {tab: this.currentTab}});
      }
    },
    updateSelectedItem(value) {
      this.selectedFilter = value;
    },
    flagAssetAsInstalled(asset) {
      this.sharedAssets = this.sharedAssets.map((sharedAsset) => {
        if (sharedAsset.id === asset.id) {
          sharedAsset.installed = true;
        }

        return sharedAsset;
      });
    }
  }
};
</script>

<style lang="scss" scoped>
@import "../../styles/variables/variables";

.store_container {
  padding: $pagePaddingVertical 0;
}

::v-deep .b-tabs .tab-content .tab-item {
  overflow: hidden;
}

// This styles the radio buttons
::v-deep .b-radio.radio.button {
  background-color: $background;
  color: $text-muted;

  &.is-light {
    background-color: rgba(55, 59, 60, 0.5);
    color: $white;
  }

  &.is-focused,
  &:focus {
    box-shadow: none;
  }
}

.upgrade_message {
  max-width: 700px;
  margin: 0 auto;
  padding: 4rem 0 2rem;

  &--heading {
    margin-bottom: 1.3rem;
    font-size: 2rem;
    font-weight: bold;
    text-align: center;
  }

  &--content {
    margin-bottom: 10px;
    font-size: 1.125rem;
    line-height: 1.4;

    a {
      font-weight: bold;
    }
  }

  &--button {
    margin-top: 1.5rem;
  }
}
</style>
